/**
 * 统一错误消息提取工具(兼容 Pydantic v2 422 list[obj] 响应)
 * R6-P0-1: 接 fallback 兜底,后端未返 detail 时显示上下文相关提示,
 * 而不是统一塌缩成"操作失败"。
 */
function formatErrorDetail(detail) {
  if (typeof detail === 'string') return detail || null
  if (Array.isArray(detail)) {
    const messages = detail.map(item => {
      if (typeof item === 'string') return item
      if (item?.msg) return item.msg
      if (item?.message) return item.message
      try {
        return JSON.stringify(item)
      } catch {
        return String(item)
      }
    }).filter(Boolean)
    return messages.join('; ') || null
  }
  if (detail?.msg) return detail.msg
  if (detail?.message) return detail.message
  return null
}

// R-audit-2026-10-05 sec-20: 错误白名单 — 只返以下安全字段,避免后端意外泄露
// internal_stack/path/query/column 等字段意外通过 detail 对象传到 UI
const _SAFE_ERROR_KEYS = ['detail', 'message', 'msg', 'request_id', 'rid']

export function getErrorMessage(err, fallback = '操作失败') {
  const responseData = err?.response?.data
  const rawDetails = [
    responseData?.detail,
    err?.data?.detail,
    err?.detail,
    typeof responseData === 'string' || Array.isArray(responseData) ? responseData : undefined,
    typeof err === 'string' || Array.isArray(err) ? err : undefined,
  ]

  for (const detail of rawDetails) {
    const message = formatErrorDetail(detail)
    if (message) return message
  }

  const responseMessage = formatErrorDetail(responseData?.message)
  if (responseMessage) return responseMessage
  const errorMessage = formatErrorDetail(err?.message)
  if (errorMessage) return errorMessage
  // sec-20: 不再 JSON.stringify(err) — 未知形状可能含 PII / 内部 stack
  // 改为只读 _SAFE_ERROR_KEYS 白名单字段
  for (const key of _SAFE_ERROR_KEYS) {
    const v = err?.[key]
    if (typeof v === 'string' && v) return v
  }
  return fallback
}

// 注:历史 R6 时期曾用 `formatError` 作为 `getErrorMessage` 的别名,31 处混用
// 造成新人阅读困难。R16 R10 P0-5 删除别名,统一以 getErrorMessage 命名。

export { exportToXlsx } from './exportToXlsx'

// R-perf-2026-10-13: 安全 Number 转换 — localStorage / URL / 表单值被污染为非数字字符串时
// (如 'foo' / 'NaN' / '10bar' 截断),Number() 会返回 NaN,后续 .some(p.id === NaN) 永远 false
// → 用户卡死无法进入项目。统一用 safeInt() 过滤,返回 null 而不是 NaN。
export function safeInt(value) {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? n : null
}

// R-perf-2026-10-13: 安全 JSON.parse — 损坏 JSON 静默吞错导致 broken column 渲染失败。
// 返回 parsed 值或 fallback,prod 也保留 dev log。
export function safeJsonParse(text, fallback = null, logKey = '') {
  if (text == null || text === '') return fallback
  try {
    return JSON.parse(text)
  } catch (e) {
    if (import.meta.env.DEV) console.warn(`[safeJsonParse${logKey ? ` ${logKey}` : ''}] parse failed`, e)
    return fallback
  }
}
